import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, CircleOff, PauseCircle, XCircle } from "lucide-react";
import { ChartCard, RankedBarChart, markGlowShadow } from "../../components/ChartsComponent";
import type { RankedBarDatum } from "../../components/ChartsComponent";
import { cycleSortDir, DataTable, Column } from "../../components/DataTable";
import { Modal } from "../../components/Modal";
import { RowsPerCardSelect } from "../../components/RowsPerCardSelect";
import { SearchInput } from "../../components/SearchInput";
import { Button } from "../../components/ui/button";
import { HEX, TILE_CLASS } from "./shared";

export type FreshnessStatus = "Healthy" | "Stale" | "Failed" | "Unknown";

export interface FreshnessItem {
  tool: string;
  status: FreshnessStatus;
  lastSuccessful: string;
  expected?: string;
  /** Failed items only; may be more recent than (and disagree with) `lastSuccessful`. */
  latestAttempt?: string;
  /** Why the tool isn't Healthy; absent for Healthy. */
  note?: string;
  /** Minutes since last touch; drives sort order (not displayed directly). */
  recencyMinutes: number;
}

// `warning` from HEX is deliberately unused here -- reserved for reporting cadence, not a flat status state.
const STATUS_META: Record<FreshnessStatus, { color: string; icon: typeof CheckCircle2 }> = {
  Healthy: { color: HEX.healthy, icon: CheckCircle2 },
  Stale: { color: HEX.stale, icon: PauseCircle },
  Failed: { color: HEX.critical, icon: XCircle },
  Unknown: { color: HEX.neutral, icon: CircleOff },
};

const FRESHNESS_DESCRIPTIONS: Record<FreshnessStatus, string> = {
  Healthy: "Data from this tool is up to date. The last sync finished successfully within the past 24 hours.",
  Stale: "Data from this tool may be out of date. The last successful sync was more than 24 hours ago and no newer data has arrived since.",
  Failed: "The most recent sync with this tool did not complete. Information shown for this tool may be missing or out of date until the issue is fixed.",
  Unknown: "This tool hasn't synced any data yet. It may still need to be set up or connected.",
};

const FRESHNESS_STATUSES: FreshnessStatus[] = ["Healthy", "Stale", "Failed", "Unknown"];

// Backend status strings are trusted by type but not validated at runtime; falls back to
// the neutral "Unknown" styling instead of crashing on an unrecognized value.
function getStatusMeta(status: FreshnessStatus) {
  return STATUS_META[status] ?? STATUS_META.Unknown;
}

export type FreshnessSlice = {
  name: FreshnessStatus;
  value: number;
  color: string;
  icon: typeof CheckCircle2;
};

/** Built from the live `/agent-health/integration/freshness/summary` counts. */
export function buildFreshnessSlices(
  counts: Partial<Record<FreshnessStatus, number>>,
): FreshnessSlice[] {
  return FRESHNESS_STATUSES.map((status) => ({
    name: status,
    value: counts[status] ?? 0,
    color: STATUS_META[status].color,
    icon: STATUS_META[status].icon,
  }));
}

/** Colour tracks the percentage itself, not a fixed "healthy" green, so a mostly-stale fleet reads as a warning. */
function meterColorForPercent(percent: number): string {
  if (percent >= 80) return HEX.healthy;
  if (percent >= 50) return HEX.stale;
  return HEX.critical;
}

/** Outline-only progress meter, matching the dashboard's other chart marks (no solid fill). */
function FreshnessMeter({ percent, color }: { percent: number; color: string }) {
  const clamped = Math.min(100, Math.max(0, percent));
  // Show a thin sliver at 0% so critical reads as red, not as an empty/no-data track.
  const isZero = clamped === 0;

  return (
    <div className="flex items-center gap-2.5">
      <div
        className="relative h-3 w-full rounded-full border"
        style={{
          borderColor: "color-mix(in srgb, var(--muted-foreground) 35%, transparent)",
          background: "transparent",
        }}
      >
        {!isZero && (
          <div
            className="absolute inset-y-0 left-0 rounded-r-full border transition-all duration-500 ease-out"
            style={{
              width: `${clamped}%`,
              background: "transparent",
              borderColor: color,
              boxShadow: markGlowShadow(color),
            }}
          />
        )}
      </div>
      <span
        className="shrink-0 text-[11px] font-semibold tabular-nums"
        style={{ color: isZero ? HEX.critical : "var(--foreground)" }}
      >
        {clamped}%
      </span>
    </div>
  );
}

/** Headline + fresh-share meter up top, then a per-state bar chart (click opens that state's roster). */
export function DataFreshnessCard({ slices, items }: { slices: FreshnessSlice[]; items: FreshnessItem[] }) {
  const byName = Object.fromEntries(slices.map((s) => [s.name, s.value]));
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const needsAttention = (byName.Stale ?? 0) + (byName.Failed ?? 0) + (byName.Unknown ?? 0);
  const freshPct = total > 0 ? Math.round(((byName.Healthy ?? 0) / total) * 100) : 0;
  const [openStatus, setOpenStatus] = useState<FreshnessStatus | null>(null);

  const chartData: RankedBarDatum[] = slices.map((slice) => ({
    name: slice.name,
    value: slice.value,
    color: slice.color,
  }));

  return (
    <ChartCard title="Tool Integration Health Status" divider className={`h-full w-full flex flex-col ${TILE_CLASS}`}>
      {total > 0 && (
        <>
          <p className="text-xs font-medium leading-snug text-muted-foreground">
            {needsAttention > 0
              ? `${needsAttention} integration${needsAttention === 1 ? "" : "s"} need attention`
              : "All integrations reporting on schedule"}
          </p>
          <FreshnessMeter percent={freshPct} color={meterColorForPercent(freshPct)} />
        </>
      )}

      <RankedBarChart
        data={chartData}
        orientation="vertical"
        height={250}
        stretch
        valueLabel="Integrations"
        tooltipDescription={(name) => FRESHNESS_DESCRIPTIONS[name as FreshnessStatus] ?? ""}
        onBarClick={(d) => setOpenStatus(d.name as FreshnessStatus)}
        emptyMessage="No integrations match the selected filters"
        ariaLabel="Tool integration health status by count"
        className={total > 0 ? "mt-4" : undefined}
        xAxisLabel="Status"
        yAxisLabel="Integrations"
      />

      <FreshnessAllModal
        open={openStatus !== null}
        onClose={() => setOpenStatus(null)}
        items={items}
        statusFilter={openStatus}
      />
    </ChartCard>
  );
}

const FRESHNESS_ROW_LIMIT = 5;

/** Only includes fields present on the item, so a Healthy tool's card doesn't show blank rows. */
function freshnessCardLines(item: FreshnessItem): { label: string; value: string; attention?: boolean }[] {
  const lines: { label: string; value: string; attention?: boolean }[] = [];
  if (item.latestAttempt) {
    lines.push({ label: "Latest attempt", value: item.latestAttempt, attention: true });
  }
  lines.push({ label: "Last successful", value: item.lastSuccessful });
  if (item.expected) {
    lines.push({ label: "Expected", value: item.expected });
  }
  if (item.note) {
    lines.push({ label: item.status === "Failed" ? "Error" : "Status", value: item.note, attention: true });
  }
  return lines;
}

function FreshnessStatusPill({ status }: { status: FreshnessStatus }) {
  const meta = getStatusMeta(status);
  return (
    <span
      className="inline-flex w-[76px] shrink-0 items-center justify-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold"
      style={{ background: `color-mix(in srgb, ${meta.color} 15%, transparent)`, color: meta.color }}
    >
      <meta.icon className="size-2.5" strokeWidth={2.5} />
      {status}
    </span>
  );
}

function FreshnessCard({ item }: { item: FreshnessItem }) {
  const color = getStatusMeta(item.status).color;
  const lines = freshnessCardLines(item);

  return (
    <div
      className="group relative overflow-hidden rounded-lg border border-border bg-muted/30 p-3 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:bg-card hover:shadow-[0_10px_24px_-10px_var(--accent-glow)] hover:[border-color:color-mix(in_srgb,var(--accent)_50%,var(--border))]"
      style={
        {
          "--accent": color,
          "--accent-glow": `color-mix(in srgb, ${color} 45%, transparent)`,
        } as React.CSSProperties
      }
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-12 opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100"
        style={{ background: `linear-gradient(to bottom, color-mix(in srgb, ${color} 10%, transparent), transparent)` }}
      />
      <div className="relative flex items-start justify-between gap-2">
        <p className="min-w-0 truncate text-xs font-bold leading-tight text-foreground">{item.tool}</p>
        <FreshnessStatusPill status={item.status} />
      </div>
      <dl className="relative mt-2 flex flex-col gap-1">
        {lines.map((line) => (
          <div key={line.label} className="flex items-baseline justify-between gap-2">
            <dt className="shrink-0 text-[11px] font-medium text-muted-foreground">{line.label}</dt>
            <dd
              className="min-w-0 truncate text-right text-[11px] font-semibold"
              style={{ color: line.attention ? color : "var(--foreground)" }}
            >
              {line.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

const FRESHNESS_TABLE_COLUMNS: Column<FreshnessItem>[] = [
  {
    key: "tool",
    header: "Tool",
    sortable: true,
  },
  {
    key: "status",
    header: "Status",
    sortable: true,
    render: (row) => <FreshnessStatusPill status={row.status} />,
  },
  {
    key: "lastSuccessful",
    header: "Last Successful",
    sortable: true,
    render: (row) => <span className="tabular-nums text-muted-foreground">{row.lastSuccessful || "-"}</span>,
  },
  {
    key: "note",
    header: "Reason",
    sortable: true,
    render: (row) =>
      row.note ? (
        <span className="font-medium" style={{ color: getStatusMeta(row.status).color }}>
          {row.note}
        </span>
      ) : (
        <span className="text-muted-foreground">-</span>
      ),
  },
];

/** Default sort: tools needing attention first (Failed > Unknown > Stale), Healthy falls back to recency. */
const STATUS_PRIORITY: Record<FreshnessStatus, number> = { Failed: 0, Unknown: 1, Stale: 2, Healthy: 3 };
function statusPriority(status: FreshnessStatus): number {
  return STATUS_PRIORITY[status] ?? STATUS_PRIORITY.Unknown;
}

function FreshnessAllModal({
  open,
  onClose,
  items,
  statusFilter,
}: {
  open: boolean;
  onClose: () => void;
  items: FreshnessItem[];
  /** When set, scopes the modal to just this status instead of the full roster. */
  statusFilter?: FreshnessStatus | null;
}) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Reopening for a different status shouldn't carry over prior search/sort/page state.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setSortKey(null);
    setSortDir("asc");
    setPage(0);
  }, [open, statusFilter]);

  const handleSort = (key: string) => {
    const next = cycleSortDir(sortKey, sortDir, key);
    setSortKey(next.sortKey);
    setSortDir(next.sortDir);
    setPage(0);
  };

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = items.filter(
      (item) =>
        (!statusFilter || item.status === statusFilter) &&
        (!q || String(item.tool ?? "").toLowerCase().includes(q)),
    );
    if (!sortKey) {
      return [...filtered].sort(
        (a, b) => statusPriority(a.status) - statusPriority(b.status) || a.recencyMinutes - b.recencyMinutes,
      );
    }
    return [...filtered].sort((a, b) => {
      // "Last Successful" sorts by recencyMinutes, not the display string (which won't compare chronologically).
      const av =
        sortKey === "status"
          ? statusPriority(a.status)
          : sortKey === "lastSuccessful"
            ? a.recencyMinutes
            : sortKey === "note"
              ? (a.note ?? "")
              : a.tool;
      const bv =
        sortKey === "status"
          ? statusPriority(b.status)
          : sortKey === "lastSuccessful"
            ? b.recencyMinutes
            : sortKey === "note"
              ? (b.note ?? "")
              : b.tool;
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [items, query, sortKey, sortDir, statusFilter]);

  const pagedRows = rows.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={statusFilter ? `${statusFilter} Integrations` : "All Integrations"}
      variant="wide"
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={query}
            onChange={(val) => {
              setQuery(val);
              setPage(0);
            }}
            placeholder="Search"
            className="flex-1 min-w-[200px] max-w-sm"
          />
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            <RowsPerCardSelect
              value={pageSize}
              onChange={(n) => {
                setPageSize(n);
                setPage(0);
              }}
              label="Rows per page"
            />
          </div>
        </div>

        <div className="mt-3">
          <DataTable
            maxHeight="500px"
            columns={statusFilter ? FRESHNESS_TABLE_COLUMNS.filter((c) => c.key !== "status") : FRESHNESS_TABLE_COLUMNS}
            data={pagedRows}
            emptyText="No integrations match your filters"
            page={page}
            pageSize={pageSize}
            totalItems={rows.length}
            onPageChange={setPage}
            itemLabel="integrations"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
          />
        </div>
      </div>
    </Modal>
  );
}

/** Row of the most recently active tools, capped at {@link FRESHNESS_ROW_LIMIT}; "View all" opens the full roster. */
export function FreshnessDetailBoard({ items = [] }: { items?: FreshnessItem[] }) {
  const [showAll, setShowAll] = useState(false);
  const sorted = [...items].sort((a, b) => a.recencyMinutes - b.recencyMinutes);
  const latest = sorted.slice(0, FRESHNESS_ROW_LIMIT);
  const hiddenCount = sorted.length - latest.length;

  return (
    <section className={`p-4 ${TILE_CLASS}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold leading-tight text-foreground">Recent Tool Integration Activity Log</p>
        </div>
        {hiddenCount > 0 && (
          <Button variant="link" size="none"
            type="button"
            onClick={() => setShowAll(true)}
            className="shrink-0 text-xs font-bold text-blue-600 underline decoration-blue-600/50 underline-offset-2 transition-colors hover:text-blue-700 hover:decoration-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-blue-400 dark:decoration-blue-400/50 dark:hover:text-blue-300 dark:hover:decoration-blue-300"
          >
            View all {sorted.length}
          </Button>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {latest.map((item) => (
          <FreshnessCard key={item.tool} item={item} />
        ))}
      </div>

      <FreshnessAllModal open={showAll} onClose={() => setShowAll(false)} items={items} />
    </section>
  );
}
